import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Archive } from "lucide-react";
import { ApiError } from "@finora/api-client";
import type { EarhivAdminSecretDto, EarhivAdminStorageItemDto } from "@finora/api-client";
import { Button, EmptyState, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";

function fmtSize(bytes: number | null): string {
  if (bytes === null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function fmtDate(v: string | null): string {
  if (!v) return "—";
  return new Date(v).toLocaleDateString("sr-Latn-RS");
}

function Row({ item, onChanged }: { item: EarhivAdminStorageItemDto; onChanged: () => void }) {
  const [quota, setQuota] = useState(String(item.quotaGb));
  const [secret, setSecret] = useState<EarhivAdminSecretDto | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const saveQuota = useMutation({
    mutationFn: () => api.setEarhivAdminQuota(item.companyId, Number(quota)),
    onSuccess: () => {
      setErr(null);
      onChanged();
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : "Greška pri čuvanju."),
  });
  const reveal = useMutation({
    mutationFn: () => api.revealEarhivAdminSecret(item.companyId),
    onSuccess: (r) => {
      setErr(null);
      setSecret(r);
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : "Greška."),
  });

  const used = item.garageBytes ?? item.usedBytes;
  const pct = Math.min(100, Math.round((used / (item.quotaGb * 1024 * 1024 * 1024)) * 100));

  return (
    <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-semibold text-slate-900">{item.companyName}</p>
          <p className="text-xs text-slate-500">PIB {item.taxId} · napravljeno {fmtDate(item.provisionedAt)}</p>
        </div>
        <code className="rounded bg-slate-100 px-2 py-1 text-xs">{item.bucket}</code>
      </div>

      <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100">
        <div className={`h-full ${pct >= 90 ? "bg-red-500" : "bg-primary"}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-1 flex flex-wrap justify-between gap-2 text-xs text-slate-600">
        <span>
          Garage: {fmtSize(item.garageBytes)} ({item.garageObjects ?? "—"} objekata) · Baza: {fmtSize(item.usedBytes)} ({item.files} fajlova)
        </span>
        <span>Limit {item.quotaGb} GB ({pct}%)</span>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
        <span className="text-slate-600">Ključ:</span>
        <code className="rounded bg-slate-100 px-2 py-1 text-xs">{item.accessKeyId}</code>
        <Button variant="secondary" onClick={() => reveal.mutate()} disabled={reveal.isPending}>
          Prikaži tajni ključ
        </Button>
        <span className="ml-auto flex items-center gap-2">
          <input
            type="number"
            min={1}
            value={quota}
            onChange={(e) => setQuota(e.target.value)}
            className="w-24 rounded-lg border border-border px-2 py-1 text-sm"
          />
          <span className="text-slate-600">GB</span>
          <Button variant="secondary" onClick={() => saveQuota.mutate()} disabled={saveQuota.isPending || Number(quota) === item.quotaGb}>
            Sačuvaj prostor
          </Button>
        </span>
      </div>

      {secret && (
        <div className="mt-3 rounded-lg bg-amber-50 p-3 text-xs text-amber-900">
          <p className="mb-1 font-semibold">Tajni ključ (otvaranje je upisano u log)</p>
          <p>Adresa: <code>{secret.endpoint}</code> · Bucket: <code>{secret.bucket}</code></p>
          <p>Access key: <code>{secret.accessKeyId}</code></p>
          <p>Secret key: <code className="select-all">{secret.secretAccessKey}</code></p>
          <button type="button" className="mt-2 underline" onClick={() => setSecret(null)}>Sakrij</button>
        </div>
      )}
      {err && <p className="mt-2 text-sm text-red-700">{err}</p>}
    </div>
  );
}

export function EarhivStoragePage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["earhiv-admin-storage"], queryFn: () => api.getEarhivAdminStorage() });

  if (q.isLoading) return <Skeleton className="h-40 w-full" />;
  if (q.isError || !q.data) return <EmptyState icon={Archive} title="Greška" description="Pregled skladišta nije dostupan." />;
  const d = q.data;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-semibold text-slate-900">E-arhiv S3 skladište</h1>
        <Button variant="secondary" onClick={() => qc.invalidateQueries({ queryKey: ["earhiv-admin-storage"] })}>
          Osveži
        </Button>
      </div>

      {!d.configured && (
        <p className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">Skladište nije podešeno (nedostaju podaci za Garage u .env).</p>
      )}
      {d.configured && !d.garageOnline && (
        <p className="rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800">Garage trenutno ne odgovara – prikazani su samo podaci iz baze.</p>
      )}

      <div className="grid gap-3 sm:grid-cols-4">
        {[
          ["Firmi sa bucketom", String(d.totals.companies)],
          ["Fajlova", String(d.totals.files)],
          ["Zauzeto (baza)", fmtSize(d.totals.usedBytes)],
          ["Zauzeto (Garage)", fmtSize(d.totals.garageBytes)],
        ].map(([label, value]) => (
          <div key={label} className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
            <p className="text-xs text-slate-500">{label}</p>
            <p className="text-lg font-semibold text-slate-900">{value}</p>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-500">S3 adresa: {d.endpoint || "—"}</p>

      {d.items.length === 0 ? (
        <EmptyState icon={Archive} title="Još nema firmi" description="Bucket se pravi sam kad firma prvi put pošalje fajl u e-arhiv." />
      ) : (
        <div className="space-y-3">
          {d.items.map((i) => (
            <Row key={i.companyId} item={i} onChanged={() => qc.invalidateQueries({ queryKey: ["earhiv-admin-storage"] })} />
          ))}
        </div>
      )}
    </div>
  );
}
